import { useMemo } from "react";
import { useAuth } from "../../context/AuthContext";
import { useTabParam } from "../../lib/useTabParam";
import type { TeamRole } from "../../lib/types";

// Existing tabs (reused directly for Sam + Executive)
import QueueTab from "./QueueTab";
import TodayRouteTab from "./TodayRouteTab";
import CompletedTab from "./CompletedTab";
import ReportsTab from "./ReportsTab";
import DroneFlightsTab from "./DroneFlightsTab";
import UnableToFlyTab from "./UnableToFlyTab";

// Role-specific views
import AlexASMView from "./AlexASMView";
import JoyOfficeView from "./JoyOfficeView";
import JenniferQCView from "./JenniferQCView";
import ExecutiveDroneView from "./ExecutiveDroneView";

type TabConfig = { key: string; label: string; component: React.ComponentType };

// ── View-as options (the role filter dropdown) ──
type ViewAsKey = "sam" | "jeremy" | "jennifer" | "alex" | "joy" | "executive";

const VIEW_AS_OPTIONS: Array<{ key: ViewAsKey; label: string; emoji: string }> = [
  { key: "alex", emoji: "👔", label: "Alex (ASM)" },
  { key: "sam", emoji: "🛩️", label: "Sam (Pilot)" },
  { key: "jeremy", emoji: "📋", label: "Jeremy (Reports)" },
  { key: "jennifer", emoji: "🔍", label: "Jennifer (QC)" },
  { key: "joy", emoji: "📞", label: "Joy (Office)" },
  { key: "executive", emoji: "📊", label: "Executive" },
];

function getTabsForView(view: ViewAsKey): TabConfig[] {
  switch (view) {
    case "sam":
      return [
        { key: "received", label: "Appointment Received", component: QueueTab },
        { key: "scheduled", label: "Appointment Scheduled", component: TodayRouteTab },
        { key: "complete", label: "Appointment Complete", component: CompletedTab },
      ];
    case "jeremy":
      return [
        { key: "reports", label: "Report Processing", component: ReportsTab },
      ];
    case "jennifer":
      return [
        { key: "qc", label: "QC Review", component: JenniferQCView },
        { key: "reports", label: "Report Pipeline", component: ReportsTab },
      ];
    case "alex":
      return [
        { key: "asm", label: "ASM Review", component: AlexASMView },
        { key: "pipeline", label: "Full Pipeline", component: DroneFlightsTab },
      ];
    case "joy":
      return [
        { key: "office", label: "Climb Auth & Insufficient", component: JoyOfficeView },
      ];
    case "executive":
    default:
      return [
        { key: "overview", label: "Overview", component: ExecutiveDroneView },
        { key: "flights", label: "All Flights", component: DroneFlightsTab },
        { key: "reports", label: "Reports", component: ReportsTab },
        { key: "unable", label: "Unable to Fly", component: UnableToFlyTab },
      ];
  }
}

/** Map the logged-in user's TeamRole to their default view */
function defaultViewForRole(role: TeamRole | undefined): ViewAsKey {
  switch (role) {
    case "drone_pilot":
      return "sam";
    case "report_specialist":
      return "jeremy";
    case "qc":
    case "roof_director":
    case "director_of_operations":
      return "jennifer";
    case "asm":
      return "alex";
    case "office":
      return "joy";
    default:
      return "executive";
  }
}

export default function DroneOps() {
  const { role, member } = useAuth();
  // A user can be a drone pilot via their primary role OR their secondary_role
  // (e.g. Sam Houston is roof_specialist + secondary drone_pilot). Either should
  // land them on the pilot ("sam") view where their scheduled flights live.
  const myDefault =
    role === "drone_pilot" || member?.secondary_role === "drone_pilot"
      ? "sam"
      : defaultViewForRole(role);
  // viewAs + activeTab live in the URL so back-from-deal returns here intact
  const [viewAs, setViewAs] = useTabParam<ViewAsKey>("view", myDefault, [
    "sam", "jeremy", "jennifer", "alex", "joy", "executive",
  ]);
  const tabs = useMemo(() => getTabsForView(viewAs), [viewAs]);
  const validTabKeys = useMemo(() => tabs.map((t) => t.key), [tabs]);
  const defaultTabKey = tabs[0]?.key ?? "overview";
  const [activeTab, setActiveTab] = useTabParam<string>("tab", defaultTabKey, validTabKeys);

  // When viewAs changes, reset to first tab of new view
  const handleViewChange = (newView: ViewAsKey) => {
    setViewAs(newView);
    const newTabs = getTabsForView(newView);
    setActiveTab(newTabs[0]?.key ?? "overview");
  };

  const activeConfig = tabs.find((t) => t.key === activeTab) ?? tabs[0];
  const ActiveComponent = activeConfig?.component;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-2">
        <h1 className="text-2xl font-semibold text-bardin-ink">Drone Inspection Board</h1>
        <p className="text-sm text-slate-500 mt-1">
          3-day SLA from appointment. 7 min per stop (1 setup · 5 fly · 1 pack).
        </p>
      </div>

      {/* Role bar — matches C3 artifact */}
      <div className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 mb-4 overflow-x-auto whitespace-nowrap flex gap-1.5">
        {VIEW_AS_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => handleViewChange(opt.key)}
            className={
              "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition flex-shrink-0 " +
              (viewAs === opt.key
                ? "bg-bardin-teal text-white border-bardin-teal shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
            }
          >
            <span>{opt.emoji}</span>
            {opt.label}
            {opt.key === myDefault ? " (You)" : ""}
          </button>
        ))}
      </div>

      {tabs.length > 1 && (
        <div className="border-b border-slate-200 mb-4">
          <nav className="flex gap-1 overflow-x-auto" aria-label="Drone tabs">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={
                  "px-4 py-2 text-sm font-medium rounded-t -mb-px border-b-2 whitespace-nowrap " +
                  (activeTab === t.key
                    ? "text-bardin-teal border-bardin-teal"
                    : "text-slate-500 border-transparent hover:text-slate-700")
                }
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      <div>{ActiveComponent && <ActiveComponent />}</div>
    </div>
  );
}
